import { useField } from 'formik';
import { TeamOutlined } from '@ant-design/icons';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import FormAutocomplete from 'components/new-ui/composed/formAutocomplete/FormAutocomplete';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import { CreateJobFormSteps } from 'views/jobs/types';
import { CreateJobFormFields } from 'views/jobs/validationSchemas';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getTeamById, listTeams } from 'store/teams/client';
import { GetTeamsParams, Team, TeamStatuses } from 'store/teams/types';
import Notification from 'components/ui/base/notification/Notification';
import { debounce } from 'lodash';
import { DefaultOptionType } from 'antd/lib/select';
import useSession from 'hooks/useSession';
import { Individual, IndividualType } from 'store/individuals/individual';
import useIndividuals from 'hooks/useIndividuals';

const fieldName = 'team.teamId';

const defaultParams: GetTeamsParams = {
    page: 0,
    size: 999,
    status: TeamStatuses.ACTIVE,
};
// Note: We display this form section only for Freight Forwarder company type
// If the user is Tenant Admin, we list all teams, otherwise, display current user's team options
// If the form is created from within a team (if it has teamId in search params), we hide this section
const TeamSection = () => {
    const { userInfo } = useSession();
    const { getIndividual } = useIndividuals();

    const [field, meta, helpers] = useField(fieldName);

    const { isIdle, isActive, isComplete, currentStepConfig, resetStep } =
        useMultiStepFormContext<CreateJobFormFields>();

    const [teams, setTeams] = useState<Team[]>([]);
    const [teamInput, setTeamInput] = useState('');
    const [hasLoadedForNonAdmin, setHasLoadedForNonAdmin] = useState(false);

    const stepKey = CreateJobFormSteps.TEAM;
    const disabled = currentStepConfig?.disabled ?? false;
    const sectionTitle = 'Team';
    const isActiveStep = isActive(stepKey);

    const isFreightAdmin = !!(userInfo && userInfo.role === IndividualType.FREIGHT_FORWARDER_ADMIN);

    const handleReset = () => {
        resetStep(stepKey, 'teamId');
        setTeamInput('');
    };

    const handleSearchTeams = (val: string) => {
        setTeamInput(val);
        if (isFreightAdmin) {
            debouncedFetchTeams(val);
        }
    };

    const handleClearTeam = () => {
        setTeamInput('');
        helpers.setValue(undefined);
    };

    const handleSelectTeam = (val: string, option?: DefaultOptionType) => {
        setTeamInput(String(option?.label ?? ''));
        helpers.setValue(val);
        debouncedFetchTeams.cancel();
    };

    const fetchTeams = useCallback(
        async (query?: string) => {
            try {
                if (isFreightAdmin) {
                    const params: GetTeamsParams = {
                        ...defaultParams,
                        page: 0,
                        ...(query?.trim() ? { query: query.trim() } : {}),
                    };
                    const res = await listTeams(params);
                    setTeams(res.list ?? []);
                } else {
                    if (hasLoadedForNonAdmin || !userInfo?.individualId) return;

                    const individual: Individual = await getIndividual(userInfo.individualId);
                    const activeTeamId = individual.teamMemberships[0].teamId;
                    const activeTeam = await getTeamById(activeTeamId);
                    setTeams([activeTeam]);
                    setHasLoadedForNonAdmin(true);
                }
            } catch (err) {
                Notification({
                    type: 'error',
                    placement: 'top',
                    messageTitle: 'Error',
                    description: 'Failed to load teams',
                });
                setTeams([]);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [field.value, getIndividual, hasLoadedForNonAdmin, isFreightAdmin, userInfo?.individualId]
    );

    const debouncedFetchTeams = useMemo(() => debounce((q: string) => fetchTeams(q), 300), [fetchTeams]);

    const teamsOptions: DefaultOptionType[] = useMemo(() => {
        if (!teams) {
            return [];
        }

        // Note: For some reason, when getting teams by query, the BE returns duplicates
        const teamsMap = new Map(teams.map((t) => [t.id, t]));
        const uniqueTeams = Array.from(teamsMap.values());

        return uniqueTeams.map((t) => ({
            label: t.name,
            value: t.id,
        }));
    }, [teams]);

    // Get initial teams options
    useEffect(() => {
        if (isActiveStep) {
            fetchTeams();
        }

        return () => debouncedFetchTeams.cancel();
    }, [debouncedFetchTeams, fetchTeams, isActiveStep]);

    if (isIdle(stepKey)) {
        return (
            <div>
                <FormSectionHeader title={sectionTitle} icon={<TeamOutlined />} />
            </div>
        );
    }

    if (isActive(stepKey)) {
        return (
            <div>
                <FormSectionHeader title={sectionTitle} icon={<TeamOutlined />} subtitle="Select a team" />
                <FormAutocomplete
                    fieldName={fieldName}
                    inputPlaceholder="Search team"
                    style={{ width: '100%' }}
                    fieldMeta={meta}
                    options={teamsOptions}
                    onSearch={handleSearchTeams}
                    onSelect={handleSelectTeam}
                    onClear={handleClearTeam}
                    onBlur={() => helpers.setTouched(true)}
                    value={teamInput}
                    disabled={disabled}
                />
            </div>
        );
    }

    if (isComplete(stepKey)) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <FormSectionHeader title={sectionTitle} icon={<TeamOutlined />} />
                <ReadonlyFormItem disabled={disabled} value={teamInput} onReset={handleReset} />
            </div>
        );
    }

    return null;
};

export default TeamSection;
